import dayjs from 'dayjs';
import { ETransactionPaymentStatuses, dictionaryTransactionStatus } from 'src/interfaces/transactions';
import { filterProp } from 'src/interfaces/transactionsFilters';

/** Buckets de Monto (H4): Select por rango, no inputs libres. */
export interface AmountBucket {
  key: string;
  label: string;
  amount_min: number | null;
  amount_max: number | null;
}

export const AMOUNT_BUCKETS: AmountBucket[] = [
  { key: 'all', label: 'Todos', amount_min: null, amount_max: null },
  { key: 'up_to_1000', label: 'Hasta $1.000', amount_min: null, amount_max: 1000 },
  { key: '1000_5000', label: '$1.000 – $5.000', amount_min: 1000, amount_max: 5000 },
  { key: 'over_5000', label: 'Más de $5.000', amount_min: 5000, amount_max: null }
];

/** Mapeo inverso: min/max de la URL → bucket. Solo combos exactos (ajuste 9 post-auditoría). */
export const bucketFromAmounts = (min?: number | null, max?: number | null): AmountBucket => {
  const found = AMOUNT_BUCKETS.find((b) => (b.amount_min ?? null) === (min ?? null) && (b.amount_max ?? null) === (max ?? null));
  return found ?? AMOUNT_BUCKETS[0];
};

export const PROMO_OPTIONS = [
  { key: 'all', label: 'Todas', value: null as string | null },
  { key: 'with', label: 'Con promo', value: 'true' },
  { key: 'without', label: 'Sin promo', value: 'false' }
];

/**
 * Estados ofrecidos en el panel cuando el backend no envia su propia lista.
 *
 * Los valores son los canonicos del backend (mayusculas): se mandan tal cual en
 * el filtro y se comparan contra los que devuelve el listado. Usar minusculas
 * no daba error —la validacion del BFF ignora el caso— pero no matcheaba
 * ninguna fila, asi que el filtro no traia nada sin avisar.
 */
export const LOCAL_STATUS_OPTIONS: string[] = [
  ETransactionPaymentStatuses.APPROVED,
  ETransactionPaymentStatuses.PENDING,
  ETransactionPaymentStatuses.REJECTED,
  ETransactionPaymentStatuses.REFUNDED
];

/** Etiquetas en español; reusa el diccionario con el que la tabla pinta los badges. */
export const STATUS_LABELS: Record<string, string> = dictionaryTransactionStatus;

/** Valores del form del panel (estado transitorio hasta Aplicar). */
export interface DescargaPanelFormValues {
  dateFrom: Date | null;
  dateTo: Date | null;
  status: string;
  cardType: string;
  bank: string;
  cardBrand: string;
  promo: string;
  amountBucket: string;
}

export const EMPTY_PANEL_VALUES: DescargaPanelFormValues = {
  dateFrom: null,
  dateTo: null,
  status: '',
  cardType: '',
  bank: '',
  cardBrand: '',
  promo: 'all',
  amountBucket: 'all'
};

/** Traduce los valores del form al filtro aplicado (filterProp). */
/** El backend compara por día; mandarle la hora corre el rango. */
export const FILTER_DATE_FORMAT = 'YYYY-MM-DD';

export const panelValuesToFilter = (values: DescargaPanelFormValues): filterProp => {
  const bucket = AMOUNT_BUCKETS.find((b) => b.key === values.amountBucket) ?? AMOUNT_BUCKETS[0];
  const promo = PROMO_OPTIONS.find((p) => p.key === values.promo)?.value ?? null;
  return {
    // Las fechas viajan como día, sin hora: el backend se queda con el día y le
    // suma uno al tope, así que la fecha elegida entra completa. Mandar la hora
    // corría el rango — las 23:59 locales del 14 son las 02:59 UTC del 15, y el
    // listado terminaba incluyendo todo un día de más.
    from: values.dateFrom ? dayjs(values.dateFrom).format(FILTER_DATE_FORMAT) : null,
    to: values.dateTo ? dayjs(values.dateTo).format(FILTER_DATE_FORMAT) : null,
    status: values.status || null,
    card_type: values.cardType || null,
    bank: values.bank || null,
    card_brand: values.cardBrand || null,
    promo,
    amount_min: bucket.amount_min,
    amount_max: bucket.amount_max
  };
};

/** Lee una fecha del filtro como dia local, no como instante UTC. */
const parseFilterDate = (value?: string | null): Date | null =>
  value ? dayjs(value).startOf('day').toDate() : null;

/** Rehidrata los valores del form desde un filtro aplicado (URL). */
export const filterToPanelValues = (filter: filterProp): DescargaPanelFormValues => ({
  // `new Date('2026-10-13')` se parsea como medianoche UTC, que en Argentina es
  // el 12 a las 21:00: el calendario mostraria el dia anterior al elegido.
  dateFrom: parseFilterDate(filter.from),
  dateTo: parseFilterDate(filter.to),
  status: filter.status ?? '',
  cardType: filter.card_type ?? '',
  bank: filter.bank ?? '',
  cardBrand: filter.card_brand ?? '',
  promo: PROMO_OPTIONS.find((p) => p.value === (filter.promo ?? null))?.key ?? 'all',
  amountBucket: bucketFromAmounts(filter.amount_min, filter.amount_max).key
});
